import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { observer } from "mobx-react-lite";

import Colors from "../../../Resources/Color";
import { useDevice, useScreenDimensions } from "../../_layout";
import Tooltip from "../../Components/Tooltip";
import { useTranslation } from "react-i18next";

const normalizeForSearch = (str) =>
  String(str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getScrollableNodeFromRef = (scrollContainerRef) => {
  const refCurrent = scrollContainerRef?.current;
  if (!refCurrent) return null;

  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) return scrollableNode;

  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
};

const isUsableReferenceElement = (referenceElement) => {
  if (!referenceElement) return false;

  const isDomElement =
    typeof Element !== "undefined" && referenceElement instanceof Element;

  if (isDomElement && referenceElement.isConnected === false) {
    return false;
  }

  if (typeof window !== "undefined") {
    const getRect = referenceElement.getBoundingClientRect;
    if (typeof getRect === "function") {
      const rect = getRect.call(referenceElement);
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return false;
      }
    }
  }

  return true;
};

const isEditableEventTarget = (target) => {
  if (!target || typeof window === "undefined") return false;
  const el = target;
  const tagName = (el.tagName || "").toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }
  if (el.isContentEditable) return true;
  return false;
};

const HighlightedText = ({ text, searchQuery, color = Colors.blackColor }) => {
  if (!searchQuery || !searchQuery.trim()) {
    return <Text style={{ fontSize: 13, color }}>{text}</Text>;
  }

  const normalizedText = normalizeForSearch(text);
  const normalizedQuery = normalizeForSearch(searchQuery.trim());

  // underline matching characters (greedy longest-match-first; simplified)
  const words = normalizedQuery.split(/\s+/).filter(Boolean);
  const candidates = [];
  for (let length = words.length; length >= 1; length--) {
    for (let start = 0; start <= words.length - length; start++) {
      candidates.push(words.slice(start, start + length).join(" "));
    }
  }

  candidates.sort((a, b) => {
    const aw = a.split(/\s+/).length;
    const bw = b.split(/\s+/).length;
    if (bw !== aw) return bw - aw;
    if (b.length !== a.length) return b.length - a.length;
    return a.localeCompare(b);
  });

  const underline = new Set();
  const matched = new Set();

  for (const sub of candidates) {
    if (!sub || matched.has(sub)) continue;
    let idx = 0;
    while (idx < normalizedText.length) {
      const found = normalizedText.indexOf(sub, idx);
      if (found === -1) break;
      let overlap = false;
      for (let i = found; i < found + sub.length; i++) {
        if (underline.has(i)) {
          overlap = true;
          break;
        }
      }
      if (!overlap) {
        for (let i = found; i < found + sub.length; i++) underline.add(i);
        matched.add(sub);
      }
      idx = found + 1;
    }
  }

  return (
    <Text style={{ fontSize: 13, color }}>
      {String(text)
        .split("")
        .map((ch, i) =>
          underline.has(i) ? (
            <Text
              key={i}
              style={{
                textDecorationLine: "underline",
                textDecorationColor: Colors.bubbleColor,
                textDecorationStyle: "solid",
              }}
            >
              {ch}
            </Text>
          ) : (
            <Text key={i}>{ch}</Text>
          )
        )}
    </Text>
  );
};

const PickerRow = ({
  label,
  selected,
  onPress,
  onHover,
  searchQuery,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
  disabled,
  disabledReason,
}) => {
  const rowRef = useRef(null);

  useEffect(() => {
    if (
      selected &&
      keyboardNavigationRef?.current &&
      rowRef.current &&
      scrollContainerRef?.current
    ) {
      const isFirstItem = index === 0;
      const isLastItem = index === totalResults - 1;
      const rowElement = rowRef.current;
      const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

      if (isFirstItem) {
        scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        return;
      }

      if (isLastItem) {
        scrollContainerRef.current?.scrollToEnd?.({ animated: true });
        if (scrollContainer) {
          const containerHeight = scrollContainer.clientHeight;
          const scrollHeight = scrollContainer.scrollHeight;
          scrollContainer.scrollTop = scrollHeight - containerHeight;
        }
        return;
      }

      rowElement.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [
    selected,
    index,
    totalResults,
    scrollContainerRef,
    keyboardNavigationRef,
  ]);

  const textColor = disabled ? Colors.grayColor : Colors.blackColor;
  const iconColor = disabled ? Colors.lightGrayColor : Colors.grayColor;
  const content = (
    <TouchableOpacity
      ref={rowRef}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      onMouseEnter={onHover}
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginHorizontal: 8,
        borderRadius: 10,
        backgroundColor: selected ? "rgba(63, 123, 141, 0.08)" : "transparent",
        opacity: disabled ? 0.55 : 1,
        outlineStyle: "none",
      }}
    >
      <View
        style={{ flexGrow: 1, flexShrink: 1, marginRight: 10, minWidth: 0 }}
      >
        <HighlightedText
          text={label}
          searchQuery={searchQuery}
          color={textColor}
        />
      </View>
      <Ionicons name="add-circle-outline" size={16} color={iconColor} />
    </TouchableOpacity>
  );

  if (!disabled || !disabledReason) {
    return content;
  }

  return (
    <Tooltip
      content={disabledReason}
      placement="right"
      forceOpen={selected && keyboardNavigationRef?.current === true}
    >
      <View>{content}</View>
    </Tooltip>
  );
};

const StatisticsExerciseSearchFloating = observer(
  ({
    visible,
    onClose,
    referenceElement,
    suggestedExerciseNames,
    allExerciseNames,
    unavailableExerciseNames,
    onPick,
  }) => {
    const { t } = useTranslation();
    const { screenSize } = useScreenDimensions();
    const { isMobile } = useDevice();

    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [closeButtonHovered, setCloseButtonHovered] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);

    const debounceTimerRef = useRef(null);
    const keyboardNavigationRef = useRef(false);
    const searchInputRef = useRef(null);
    const scrollViewRef = useRef(null);

    const { refs, floatingStyles, context, update } = useFloating({
      open: visible,
      onOpenChange: (open) => {
        if (!open) onClose?.();
      },
      strategy: "fixed",
      placement: "bottom-start",
      middleware: [
        offset(6),
        flip({ fallbackAxisSideDirection: "end", padding: 8 }),
        shift({ padding: 8, crossAxis: true }),
      ],
      whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
      if (isUsableReferenceElement(referenceElement)) {
        refs.setReference(referenceElement);
        return;
      }
      refs.setReference(null);
    }, [referenceElement, refs]);

    useEffect(() => {
      if (!visible) {
        setSearchQuery("");
        setSelectedIndex(0);
        setIsSearching(false);
        return;
      }

      // Avoid rendering at (0,0) when anchor disappears.
      if (!isUsableReferenceElement(referenceElement)) {
        onClose?.();
        return;
      }

      if (!isMobile) {
        setTimeout(() => {
          searchInputRef.current?.focus?.();
        }, 50);
      }

      update?.();
    }, [visible, referenceElement, isMobile, onClose, update]);

    const role = useRole(context);
    const { getFloatingProps } = useInteractions([role]);

    const hasQuery = searchQuery.trim().length > 0;

    const unavailableSet = useMemo(() => {
      if (!unavailableExerciseNames) return new Set();
      if (unavailableExerciseNames instanceof Set)
        return unavailableExerciseNames;
      if (Array.isArray(unavailableExerciseNames)) {
        return new Set(unavailableExerciseNames.filter(Boolean));
      }
      return new Set();
    }, [unavailableExerciseNames]);

    useEffect(() => {
      if (!visible) {
        setIsPositioned(false);
        return;
      }

      if (!isUsableReferenceElement(referenceElement)) {
        setIsPositioned(false);
        return;
      }

      // Wait a frame so the position is computed before showing to avoid flash.
      setIsPositioned(false);
      const frame = requestAnimationFrame(() => {
        update?.();
        setIsPositioned(true);
      });

      return () => {
        cancelAnimationFrame(frame);
        setIsPositioned(false);
      };
    }, [visible, referenceElement, update]);

    const list = useMemo(() => {
      const q = searchQuery.trim();
      const source = q.length > 0 ? allExerciseNames : suggestedExerciseNames;
      const arr = Array.isArray(source) ? source : [];
      const normalizedQ = normalizeForSearch(q);

      const filtered =
        normalizedQ.length === 0
          ? arr
          : arr.filter((name) =>
              normalizeForSearch(name).includes(normalizedQ)
            );

      const sorted = [...filtered]
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)));

      const mapped = sorted.map((name) => {
        const disabled = unavailableSet.has(String(name));
        return {
          name: String(name),
          disabled,
          disabledReason: disabled ? t("exerciseNotInAllBlocks-string") : null,
        };
      });

      if (hasQuery) {
        return mapped;
      }

      return mapped.slice(0, 15);
    }, [
      searchQuery,
      suggestedExerciseNames,
      allExerciseNames,
      unavailableSet,
      hasQuery,
      t,
    ]);

    const handleSearchChange = useCallback((text) => {
      setSearchQuery(text);
      setIsSearching(true);
      setSelectedIndex(0);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setIsSearching(false);
      }, 100);
    }, []);

    const handleKeyDown = useCallback(
      (e) => {
        const total = list.length;
        switch (e.key) {
          case "ArrowDown":
            if (total > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) => (prev < total - 1 ? prev + 1 : 0));
            }
            break;
          case "ArrowUp":
            if (total > 0) {
              e.preventDefault();
              keyboardNavigationRef.current = true;
              setSelectedIndex((prev) => (prev > 0 ? prev - 1 : total - 1));
            }
            break;
          case "Enter":
            if (
              total > 0 &&
              list[selectedIndex] &&
              !list[selectedIndex].disabled
            ) {
              e.preventDefault();
              onPick?.(String(list[selectedIndex].name));
              onClose?.();
            }
            break;
          case "Escape":
            e.preventDefault();
            onClose?.();
            break;
        }
      },
      [list, selectedIndex, onPick, onClose]
    );

    useEffect(() => {
      if (!visible) return;
      if (typeof window === "undefined") return;

      const onKeyDown = (e) => {
        const key = e?.key;
        if (
          key !== "ArrowDown" &&
          key !== "ArrowUp" &&
          key !== "Enter" &&
          key !== "Escape"
        ) {
          return;
        }

        const floatingEl = refs?.floating?.current;
        const isInsideFloating =
          !!floatingEl && typeof floatingEl.contains === "function"
            ? floatingEl.contains(e?.target)
            : false;

        if (!isInsideFloating && isEditableEventTarget(e?.target)) {
          return;
        }

        e?.preventDefault?.();
        e?.stopPropagation?.();
        handleKeyDown(e);
      };

      window.addEventListener("keydown", onKeyDown, true);
      return () => window.removeEventListener("keydown", onKeyDown, true);
    }, [handleKeyDown, visible, refs]);

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const floatingContainerStyle = {
      backgroundColor: Colors.whiteColor,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: Colors.borderColor,
      outlineStyle: "none",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      overflow: "hidden",
      minWidth: screenSize === "small" ? 320 : 400,
      maxWidth: screenSize === "small" ? "96%" : 600,
      maxHeight: 302,
      minHeight: 180,
    };

    const hasResults = list.length > 0;

    if (!visible) return null;
    if (!isUsableReferenceElement(referenceElement)) return null;
    if (!isPositioned) return null;

    return (
      <FloatingPortal>
        <FloatingFocusManager
          context={context}
          initialFocus={isMobile ? -1 : 0}
        >
          <View
            ref={refs.setFloating}
            style={[floatingStyles, floatingContainerStyle]}
            {...getFloatingProps()}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                padding: 12,
                backgroundColor: Colors.whiteColor,
                borderBottomWidth: hasResults ? 0.5 : 0,
                borderBottomColor: Colors.borderColor,
                gap: 10,
                outlineStyle: "none",
              }}
            >
              <View
                style={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: Colors.lightGrayColor,
                  borderRadius: 10,
                  borderWidth: 0.5,
                  borderColor: Colors.borderColor,
                  paddingHorizontal: 10,
                  height: 36,
                }}
              >
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={handleSearchChange}
                  onKeyPress={handleKeyDown}
                  placeholder={t("searchExercisePlaceholder-string")}
                  placeholderTextColor={Colors.grayColor}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    flexShrink: 1,
                    fontSize: isMobile ? 16 : 13,
                    color: Colors.blackColor,
                    paddingVertical: 0,
                    height: "100%",
                    outlineStyle: "none",
                  }}
                  autoCorrect={false}
                  autoCapitalize="none"
                />

                {searchQuery.length > 0 && (
                  <TouchableOpacity
                    onPress={() => {
                      handleSearchChange("");
                      searchInputRef.current?.focus?.();
                    }}
                    style={{ padding: 2, marginLeft: 6 }}
                  >
                    <Ionicons
                      name="close-circle-outline"
                      size={14}
                      color={Colors.grayColor}
                    />
                  </TouchableOpacity>
                )}
              </View>

              <TouchableOpacity
                onPress={onClose}
                onMouseEnter={() => !isMobile && setCloseButtonHovered(true)}
                onMouseLeave={() => !isMobile && setCloseButtonHovered(false)}
                style={{ padding: 4 }}
              >
                <Ionicons
                  name="close"
                  size={20}
                  color={
                    closeButtonHovered ? Colors.darkgrayColor : Colors.grayColor
                  }
                />
              </TouchableOpacity>
            </View>

            {hasResults ? (
              <>
                <ScrollView
                  ref={scrollViewRef}
                  style={{ flex: 1, outlineStyle: "none" }}
                  contentContainerStyle={{ paddingTop: 12, paddingBottom: 8 }}
                  keyboardShouldPersistTaps="handled"
                  onMouseMove={() => {
                    if (!isMobile) keyboardNavigationRef.current = false;
                  }}
                >
                  {!hasQuery && (
                    <View
                      style={{
                        paddingHorizontal: 14,
                        marginHorizontal: 8,
                        paddingBottom: 8,
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: "600",
                          color: Colors.grayColor,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {t("suggestedExercises-string")}
                      </Text>
                    </View>
                  )}

                  {list.map((item, idx) => (
                    <PickerRow
                      key={`${String(item?.name ?? "")}-${idx}`}
                      label={String(item?.name ?? "")}
                      selected={idx === selectedIndex}
                      disabled={item?.disabled}
                      disabledReason={item?.disabledReason}
                      searchQuery={searchQuery}
                      index={idx}
                      totalResults={list.length}
                      scrollContainerRef={scrollViewRef}
                      keyboardNavigationRef={keyboardNavigationRef}
                      onPress={() => {
                        if (item?.disabled) return;
                        onPick?.(String(item?.name ?? ""));
                        onClose?.();
                      }}
                      onHover={() => {
                        if (!keyboardNavigationRef.current) {
                          setSelectedIndex(idx);
                        }
                      }}
                    />
                  ))}
                </ScrollView>

                {hasResults && !isMobile && (
                  <View
                    style={{
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderTopWidth: 0.5,
                      borderTopColor: Colors.borderColor,
                      outlineStyle: "none",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 10,
                        color: Colors.grayColor,
                        textAlign: "center",
                      }}
                    >
                      ↑↓ {t("navigate-string")}
                      {"  •  "}↵ {t("select-string")}
                      {"  •  "}Esc {t("close-string")}
                    </Text>
                  </View>
                )}
              </>
            ) : (
              <View
                style={{
                  paddingVertical: 40,
                  paddingHorizontal: 24,
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: Colors.grayColor,
                    textAlign: "center",
                  }}
                >
                  {isSearching
                    ? t("searchingEllipsis-string")
                    : t("statisticsSearchPerformedExercisesPrompt-string")}
                </Text>
              </View>
            )}
          </View>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }
);

export default StatisticsExerciseSearchFloating;
